import {
  createQuery,
  createSuspenseQuery
} from "../../utils/queryFactories.js";
import { api } from "../../utils/fetch.js";
import { z } from "zod";

const ViewUsersSchema = z.array(
  z.object({
    user_id: z.number().nullish(),
    board: z.number(),
    role: z.string(),
    email: z.string().nullable(),
    name: z.string().nullable(),
    aging: z.string(),
    last_interaction_hq: z.iso.datetime().nullable(),
    last_interaction_workforce: z.iso.datetime().nullable(),
    _group_value: z.string().nullish(),
    _group_count: z.number().nullish()
  })
);

// Add discriminated union for wf and public requests
const wfRequest = z.object({
  api: z.literal("wf"),
  id: z.string()
});
const publicRequest = z.object({
  api: z.literal("public"),
  slug: z.string(),
  id: z.string()
});
const ViewUsersRequestSchema = z.discriminatedUnion("api", [
  wfRequest,
  publicRequest
]);
const ViewUsersRequestVariables = z.union([
  wfRequest.omit({ api: true }),
  publicRequest.omit({ api: true })
]);

export type ViewUsersRequest = z.infer<typeof ViewUsersRequestSchema>;
export type ViewUsersResponse = z.infer<typeof ViewUsersSchema>;

// URL mappings for wf and public APIs
const urls = {
  wf: "/views/:id/users",
  public: "/public/:slug/boardviews/:id/users"
};

export const useGetViewUsers = createQuery<
  ViewUsersResponse,
  ViewUsersRequest,
  Error
>({
  queryKey: ["ViewUsers"],
  staleTime: 1000 * 60,
  fetcher: async (variables) => {
    const { api: viewUsersAPI, ...rest } = variables;
    return api<Omit<ViewUsersRequest, "api">, ViewUsersResponse>({
      method: "GET",
      url: urls[viewUsersAPI],
      requestSchema: ViewUsersRequestVariables,
      responseSchema: ViewUsersSchema,
      body: rest
    });
  }
});

export const useViewUsersSuspense = createSuspenseQuery<
  ViewUsersResponse,
  ViewUsersRequest,
  Error
>({
  queryKey: ["ViewUsersWithSuspense"],
  staleTime: 1000 * 60,
  fetcher: async (variables) => {
    const { api: viewUsersAPI, ...rest } = variables;
    return api<Omit<ViewUsersRequest, "api">, ViewUsersResponse>({
      method: "GET",
      url: urls[viewUsersAPI],
      requestSchema: ViewUsersRequestVariables,
      responseSchema: ViewUsersSchema,
      body: rest
    });
  }
});
